/**
 * Call attempt outcome — display rules for the API's `outcome` /
 * `outcomeActor` / `outcomeReason` fields (see maja_api call-outcome.ts).
 *
 * The API derives these from what the call flow RECORDED. For calls that ended
 * before who/why was recorded, it reads them off the terminal lifecycle trigger
 * and sets `attributionInferred`; the panel says so rather than presenting them
 * as recorded.
 */

export type CallOutcome =
  | 'completed'
  | 'declined'
  | 'cancelled'
  | 'no_answer'
  | 'not_connected'
  | 'setup_failed'
  | 'other'
  | 'in_progress';

/** `GET /admin/calls?outcome=` values (plus '' = all). */
export type CallOutcomeFilter =
  | 'connected'
  | 'unconnected'
  | 'declined'
  | 'cancelled'
  | 'no_answer'
  | 'not_connected'
  | 'setup_failed'
  /** Rings nothing ever evidenced reaching the phone — see OUTCOME_FILTERS. */
  | 'not_delivered';

export const OUTCOME_FILTERS: Array<{ value: CallOutcomeFilter | 'all'; label: string }> = [
  { value: 'all', label: 'All calls' },
  { value: 'connected', label: 'Connected' },
  { value: 'unconnected', label: 'Unconnected' },
  { value: 'declined', label: 'Listener declined' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_answer', label: 'No answer (ring timeout)' },
  { value: 'not_connected', label: 'Accepted, never connected' },
  { value: 'setup_failed', label: 'Setup failed' },
  // Not a sub-set of "No answer": a ring can time out having rung perfectly
  // well. This is the one that never reached the device at all — no push
  // acknowledgement and no live socket — so the listener never heard it.
  { value: 'not_delivered', label: "Never reached the phone" },
];

const OUTCOME_LABEL: Record<CallOutcome, string> = {
  completed: 'Completed',
  declined: 'Listener declined',
  cancelled: 'Cancelled',
  no_answer: 'No answer',
  not_connected: 'Accepted, never connected',
  setup_failed: 'Setup failed',
  other: 'Not connected',
  in_progress: 'In progress',
};

/** Typed loosely: the value comes off the wire and may be absent (older API) or new. */
export function outcomeLabel(outcome: string | null | undefined): string {
  if (!outcome) return '—';
  return OUTCOME_LABEL[outcome as CallOutcome] ?? outcome;
}

/** Connected / Unconnected / In progress — the top-level status column. */
export function connectionLabel(outcome: string | null | undefined, connectedAt: string | null | undefined): string {
  if (outcome === 'in_progress') return 'In progress';
  return connectedAt ? 'Connected' : 'Unconnected';
}

/** antd Tag colour per outcome. */
export function outcomeColor(outcome: string | null | undefined): string {
  switch (outcome) {
    case 'completed':
      return 'green';
    case 'declined':
      return 'volcano';
    case 'cancelled':
      return 'gold';
    case 'no_answer':
      return 'orange';
    case 'not_connected':
    case 'setup_failed':
    case 'other':
      return 'red';
    case 'in_progress':
      return 'processing';
    default:
      return 'default';
  }
}

/** Who performed the terminal action. */
export function actorLabel(actor: string | null | undefined): string {
  switch (actor) {
    case 'user':
      return 'User';
    case 'listener':
      return 'Listener';
    case 'system':
      return 'System';
    case 'admin':
      return 'Admin';
    default:
      return 'Not recorded';
  }
}

/** An automatic stop by the caller's app (trigger `caller_app_gone`). */
export const CALLER_APP_GONE_LABEL = "Stopped automatically — the caller's app was no longer following it";

/**
 * The reason, in the words the brief asks for: who did what.
 *
 * [trigger] is the API's `outcomeTrigger` — the raw lifecycle trigger of the
 * terminal transition. It is needed because one recorded reason can cover two
 * very different events: `no_answer` is written both for a ring the listener
 * sat through and for one their phone was never told about in time
 * (`delivery_timeout`). Reporting the second as "the listener did not respond"
 * blames a listener who was never rung, so when the trigger says otherwise it
 * wins. Omit it and the labels are exactly what they were.
 */
export function outcomeReasonLabel(
  reason: string | null | undefined,
  actor: string | null | undefined,
  connected: boolean,
  trigger?: string | null,
  /**
   * Whether the listener's phone acknowledged the ring (API `ringDelivered`).
   * A ring that timed out reads completely differently depending on this: they
   * let it ring out, or it never rang. Undefined = an older API response that
   * does not report it; the wording then stays as it was.
   */
  ringDelivered?: boolean,
): string {
  const who = actor === 'listener' ? 'Listener' : actor === 'user' ? 'User' : actor === 'admin' ? 'Admin' : 'Participant';
  if (trigger === 'delivery_timeout') {
    return "Never reached the listener's phone — the ring was not delivered in time";
  }
  // The caller's app stopped a request nobody was following — not a hang-up.
  if (trigger === 'caller_app_gone') return CALLER_APP_GONE_LABEL;
  if (reason === 'no_answer' && ringDelivered !== undefined) {
    return ringDelivered
      ? "Rang on the listener's phone — they did not answer"
      : "Never reached the listener's phone — it never acknowledged the ring";
  }
  switch (reason) {
    case 'hung_up':
      return `${who} ended the call`;
    case 'cancelled':
      return connected ? `${who} ended the call` : `${who} cancelled before the call connected`;
    case 'declined':
      return 'Listener declined the call';
    case 'no_answer':
      return 'Ring timed out — the listener did not respond';
    case 'connect_timeout':
      return 'Listener accepted, but both sides never joined in time';
    case 'setup_failed':
      return actor ? `Call setup failed on the ${actor === 'user' ? "user's" : "listener's"} phone` : 'Call setup failed on a phone';
    case 'network_lost':
      return 'Connection lost — heartbeats stopped';
    case 'insufficient_balance':
      return "Caller's balance ran out";
    case 'safety_flag':
      return 'Ended by a safety flag';
    case 'admin_force_end':
      return 'Ended by an admin';
    case 'time_limit':
      return 'Time limit reached (verification call / night limit)';
    case null:
    case undefined:
    case '':
      return 'Not recorded';
    default:
      return reason;
  }
}

export interface TimelineEventIn {
  id?: string;
  fromStatus?: string | null;
  toStatus: string;
  trigger: string | null;
  createdAt: string;
}

/** The trigger of the terminal transition in a call's event list — the same
 *  value the API sends as `outcomeTrigger`, for views that hold the events but
 *  not that field. */
/** Did the listener's phone acknowledge the ring? Same rule the API uses, for
 *  views that hold the events but not the `ringDelivered` field. */
export function ringWasDelivered(events: TimelineEventIn[] | undefined): boolean {
  return (events ?? []).some((e) => e.trigger === 'ring_delivered' || e.toStatus === 'ringing');
}

export function terminalTrigger(events: TimelineEventIn[] | undefined): string | null {
  // The transition INTO the terminal status — a note written onto the call
  // after it ended (`late_accept_refused`: ended_x → ended_x) is not why it
  // ended. Same rule as the API's `terminalTransition`.
  return (
    [...(events ?? [])]
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      .find((e) => e.toStatus.startsWith('ended_') && !e.fromStatus?.startsWith('ended_'))?.trigger ?? null
  );
}

export interface TimelineStep {
  key: string;
  at: string | null;
  title: string;
  actor: string;
  /** The raw lifecycle trigger, kept visible for support. */
  trigger?: string | null;
}

/**
 * The call's lifecycle in plain words: "Call placed" (the call row's start —
 * creating a call is not a state transition, so there is no event for it),
 * then one step per recorded transition. The trigger → words map mirrors the
 * API's; an unknown trigger is shown as-is rather than dropped.
 */
export function buildCallTimeline(call: {
  startedAt?: string | null;
  events?: TimelineEventIn[];
  outcomeActor?: string | null;
  disconnectedBy?: string | null;
}): TimelineStep[] {
  const steps: TimelineStep[] = [
    { key: 'placed', at: call.startedAt ?? null, title: 'Call placed — ringing the listener', actor: 'User' },
  ];
  const endActor = actorLabel(call.outcomeActor ?? call.disconnectedBy);
  const events = [...(call.events ?? [])].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  events.forEach((e, i) => {
    const key = e.id ?? `${e.trigger}-${i}`;
    const step = (title: string, actor: string): TimelineStep => ({ key, at: e.createdAt, title, actor, trigger: e.trigger });
    switch (e.trigger) {
      case 'listener_accept':
        // One accept writes two transitions (initiated→ringing→connecting);
        // show it once, on the step into `connecting`.
        if (e.toStatus === 'connecting') steps.push(step('Listener accepted — joining the call', 'Listener'));
        return;
      case 'both_joined':
        steps.push(step('Connected — both sides joined', 'System'));
        return;
      case 'listener_reject':
        steps.push(step('Listener declined the call', 'Listener'));
        return;
      case 'caller_cancelled':
        steps.push(step('User cancelled before the call connected', 'User'));
        return;
      case 'caller_app_gone':
        steps.push(step(CALLER_APP_GONE_LABEL, 'System'));
        return;
      case 'listener_cancelled':
        steps.push(step('Listener cancelled before the call connected', 'Listener'));
        return;
      case 'superseded':
        steps.push(step('Replaced — the caller started a newer call while this ring had no screen following it', 'System'));
        return;
      case 'ring_delivered':
        // The listener's device asked `ring-status` — it only does that right
        // before showing the ring, so this is the proof the push arrived.
        steps.push(step("Ring delivered — the listener's phone received it", 'Device'));
        return;
      case 'accept_window_timeout':
        steps.push(step('No answer — ring timed out (listener took no action)', 'System'));
        return;
      case 'delivery_timeout':
        // NOT the same as the above: the push was never acknowledged, so the
        // phone had not started ringing when the call was ended.
        steps.push(step("Ring undelivered — the listener's phone never acknowledged it", 'System'));
        return;
      case 'connect_timeout':
        steps.push(step('Accepted, but never connected — connect timed out', 'System'));
        return;
      case 'client_setup_failed':
        steps.push(step('A phone reported the call could not be set up', 'Device'));
        return;
      case 'late_accept_refused':
        steps.push(step('Listener tapped Accept after the ring had ended — refused', 'Listener'));
        return;
      case 'end':
        steps.push(step('Call ended — hung up', endActor));
        return;
      case 'low_balance':
        steps.push(step("Call ended — caller's balance ran out", 'System'));
        return;
      case 'heartbeat_timeout':
        steps.push(step('Call ended — connection lost', 'System'));
        return;
      case 'admin_force_end':
        steps.push(step('Call ended by an admin', 'Admin'));
        return;
      default:
        steps.push(step(`${e.fromStatus ?? '—'} → ${e.toStatus}`, e.toStatus.startsWith('ended_') ? endActor : 'System'));
    }
  });
  return steps;
}
